//@ts-check

import React, { useMemo } from 'react';
import classnames from 'classnames';
import { getColor, getColorText } from 'store/settings';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { Spinner } from 'reactstrap';

/**
 * @typedef {object} ButtonProps
 * @property {string} [padding]
 * @property {boolean} [paddingEnable]
 * @property {true  | false} [disabled]
 * @property {"primary" | "secondary" | "outline" | "cancel" | "ai"} [variant]
 * @property {"reset" | "submit" | "button"} [type]
 * @property {string} [alpha]
 * @property {boolean} [isLoading]
 * @property {{text?: "textColor" | "buttonColor", buttonBg?: "textColor" | "buttonColor"}} [themeSetting] -default text value: textColor, buttonBg value: buttonColor
 * @property {string} [customCss]
 */

/**
 * @type { React.ForwardRefExoticComponent<ButtonProps & React.HTMLAttributes<HTMLButtonElement> & React.RefAttributes<HTMLButtonElement>>}
 */

const ThemeButton = React.forwardRef(
  (
    {
      className = '',
      padding = '10px 5px',
      paddingEnable = true,
      style,
      disabled = false,
      customCss,
      ...props
    },
    ref,
  ) => {
    const colorStatus = useSelector(getColor);
    const colorTextStatus = useSelector(getColorText);

    let btnType = {
      primary: 'dynamic-btn-clr',
      secondary: 'secondary-btn',
      outline: 'outline-btn',
      cancel: 'cancel-btn',
      ai: "ai-btn"
    };

    let lmsBtnConfig = {
      text:
        props?.themeSetting?.text === 'buttonColor'
          ? colorStatus
          : colorTextStatus,
      btnColor:
        props?.themeSetting?.buttonBg === 'textColor'
          ? colorTextStatus
          : colorStatus,
    };

    const ButtonDraf = useMemo(
      () =>
        styled.button`
          ${classnames(customCss)}
          &.dynamic-btn-clr:hover,
          &.ai-btn:hover {
            background-color: color-mix(in oklab, var(--btn-bg-clr) 80%, transparent) !important;
            color: var(--text-clr) !important;
            border-color: transparent !important;
            transition: background-color .2s ease;
          }
        `,
      [customCss],
    );

    return (
      <ButtonDraf
        data-padding={paddingEnable}
        data-animation={lmsBtnConfig.animation}
        style={{
          // @ts-ignore
          '--btn-bg-clr':
            props?.variant === 'outline'
              ? lmsBtnConfig.btnColor + props.alpha
              : lmsBtnConfig.btnColor,
          '--text-clr': lmsBtnConfig.text,
          '--dynamic-padding': padding,

          pointerEvents: disabled ? 'none' : 'auto',
          cursor: disabled ? 'not-allowed' : 'pointer',
          ...style,
        }}
        disabled={props.isLoading || disabled}
        className={classnames(
          'btn',
          btnType[props?.variant ?? 'primary'],
          className,
        )}
        ref={ref}
        {...props}
      >
        {props.isLoading && (
          <Spinner
            animation="$border"
            role="status"
            className="mr-05"
            style={{
              width: 17,
              height: 17,
            }}
          />
        )}
        {props.children}
      </ButtonDraf>
    );
  },
);

export default ThemeButton;
